import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import mapboxgl from 'mapbox-gl';

import { Box } from '@mui/material';
import * as turf from '@turf/turf';

import { LayerContext } from 'state/LayerContext';
import { useGetDatesQuery } from 'state/apiSlice';
import { useAppSelector } from 'state/hooks';
import { useGetSavedRoutesWithGeojsonQuery } from 'state/savedRouteSlices';
import { selectCurrentProjectInfo } from 'state/workflowSlice';

import { Map } from 'features/map/Map';
import { MapContext, MapContextProvider } from 'features/map/MapContext';
import { useHoverPulseAnimation } from 'features/map/hooks/useHoverPulseAnimation';
import {
  addMapLayerInDefinedStack,
  mapHasLayer,
  mapHasSource,
} from 'features/map/utils/mapCommon';
import {
  getRouteMonitorBorderLayerDefinition,
  getRouteMonitorLayerDefinition,
  getSelectedSegmentLayerArrowDefinition,
} from 'features/map/utils/mapLayerDefinitions';
import { fitBoundsToBbox } from 'features/map/utils/mapUtils';
import {
  kAlertRouteArrowLayerId,
  kMonitorRouteBorderLayerId,
  kMonitorRouteLayerId,
  selectedRouteColor,
  selectedSegmentColor,
} from 'features/map/utils/map_constants';

import { useNotificationProject } from '../useNotificationProject';
import { RouteCountDisplay } from './RouteCountDisplay';

import styles from './RouteMapDisplay.module.css';

interface RouteMapDisplayProps {
  selectedRoutes: string[];
  availableRoutes?: string[];
  hoveredRoutes?: string[];
  showRouteCount?: boolean;
}

const ARROW_OPACITY_EXPRESSION = [
  'case',
  ['boolean', ['get', 'isSelected'], false],
  1, // show arrow if isSelected true
  0, // hide arrow if isSelected false
] as mapboxgl.Expression;

function RouteMapDisplayInner({
  selectedRoutes,
  availableRoutes = [],
  hoveredRoutes = [],
  showRouteCount = false,
}: RouteMapDisplayProps) {
  const routesToShow =
    availableRoutes.length > 0 ? availableRoutes : selectedRoutes;
  const { refMap, mapReady, refStyleReady, mapStyle } = useContext(MapContext);

  const { projectSlug } = useNotificationProject();
  const { layer } = useContext(LayerContext);

  const { data: savedRoutesData } = useGetSavedRoutesWithGeojsonQuery(
    { project_slug: projectSlug, layerId: layer },
    { skip: !projectSlug || !layer },
  );

  const routeFeatures = useMemo(() => {
    if (!savedRoutesData) return [];

    return savedRoutesData
      .filter((route) => routesToShow.includes(route.id))
      .map((route) => {
        const { routeName } = route.savedRoute;
        const isSelected = selectedRoutes.includes(route.id);
        const isHovered = hoveredRoutes.includes(route.id);

        // Highlight selected routes in blue, all others in white
        const color = isSelected ? selectedRouteColor : selectedSegmentColor; // white for all routes

        return {
          type: 'Feature' as const,
          geometry: route.feature.geometry,
          id: route.id,
          properties: {
            route_length: route.feature.properties.route_length,
            cardinal_direction: route.feature.properties.cardinal_direction,
            route: route.id,
            name: routeName,
            color,
            isSelected,
            isHovered,
          },
        };
      })
      .sort((a, b) => {
        // make sure the selected routes are on top
        const aPriority = a.properties.isSelected ? 1 : 0;
        const bPriority = b.properties.isSelected ? 1 : 0;
        return aPriority - bPriority;
      });
  }, [savedRoutesData, routesToShow, selectedRoutes, hoveredRoutes]);

  const updateMapSource = useCallback((map: mapboxgl.Map, features: any[]) => {
    const data = {
      type: 'FeatureCollection' as const,
      features,
    };

    if (!mapHasSource(map, kMonitorRouteLayerId)) {
      map.addSource(kMonitorRouteLayerId, {
        type: 'geojson',
        data,
      });
    } else {
      (map.getSource(kMonitorRouteLayerId) as any).setData(data);
    }
  }, []);

  const ensureRouteLayer = useCallback((map: mapboxgl.Map) => {
    if (!mapHasLayer(map, kMonitorRouteLayerId)) {
      addMapLayerInDefinedStack(map, {
        ...getRouteMonitorLayerDefinition(mapStyle),
        source: kMonitorRouteLayerId,
      } as mapboxgl.AnyLayer);
    }
  }, []);

  const ensureBorderLayer = useCallback((map: mapboxgl.Map) => {
    if (!mapHasLayer(map, kMonitorRouteBorderLayerId)) {
      addMapLayerInDefinedStack(map, {
        ...getRouteMonitorBorderLayerDefinition(mapStyle),
        source: kMonitorRouteLayerId,
      } as mapboxgl.AnyLayer);
    }
  }, []);

  const manageArrowLayer = useCallback(
    (map: mapboxgl.Map) => {
      const hasArrowLayer = mapHasLayer(map, kAlertRouteArrowLayerId);
      const shouldShowArrows = selectedRoutes.length > 0;

      if (shouldShowArrows) {
        if (!hasArrowLayer) {
          addMapLayerInDefinedStack(map, {
            ...getSelectedSegmentLayerArrowDefinition(mapStyle),
            id: kAlertRouteArrowLayerId,
            source: kMonitorRouteLayerId,
            paint: {
              'icon-color': selectedSegmentColor,
              'icon-opacity': ARROW_OPACITY_EXPRESSION,
            },
          } as mapboxgl.AnyLayer);
        } else {
          // update opacity expression when selection changes
          map.setPaintProperty(
            kAlertRouteArrowLayerId,
            'icon-opacity',
            ARROW_OPACITY_EXPRESSION,
          );
        }
      } else if (hasArrowLayer) {
        map.removeLayer(kAlertRouteArrowLayerId);
      }
    },
    [selectedRoutes],
  );

  const redrawRoutes = useCallback(() => {
    const map = refMap.current;
    if (!map || !mapReady || !refStyleReady.current) return;

    updateMapSource(map, routeFeatures);
    ensureRouteLayer(map);
    ensureBorderLayer(map);
    manageArrowLayer(map);
  }, [
    mapReady,
    routeFeatures,
    refMap,
    refStyleReady,
    updateMapSource,
    ensureRouteLayer,
    ensureBorderLayer,
    manageArrowLayer,
  ]);

  useEffect(() => {
    if (!refMap.current || !refStyleReady.current || !mapReady) return;
    redrawRoutes();
  }, [mapReady, redrawRoutes, refMap, refStyleReady]);

  // pulsing animation for hovered routes
  useHoverPulseAnimation({
    hasData: routeFeatures.length > 0,
    shouldStopAnimation: hoveredRoutes.length === 0,
  });

  // make sure to show on the map all routes
  useEffect(() => {
    if (
      !refMap.current ||
      !mapReady ||
      !refStyleReady.current ||
      routeFeatures.length === 0
    )
      return;
    // fitting all routes on the map
    try {
      const featureCollection = turf.featureCollection(
        routeFeatures.map((route) => turf.feature(route.geometry)),
      );
      const bboxPoly = turf.bboxPolygon(turf.bbox(featureCollection));
      fitBoundsToBbox(refMap.current, bboxPoly.geometry, 0.01);
    } catch (error) {
      console.error('Error fitting bounds to routes:', error);
    }
  }, [mapReady, routeFeatures, refMap, refStyleReady]);

  return (
    <Box className={styles.mapContainer}>
      <Map
        chartShowhide={false}
        showRoutes={false}
        enableRouteSelection={false}
        drawSegmentLayer={false}
        hideControls
        extras={
          showRouteCount ? (
            <RouteCountDisplay count={selectedRoutes.length} />
          ) : undefined
        }
      />
    </Box>
  );
}

export function RouteMapDisplay({
  selectedRoutes,
  availableRoutes,
  hoveredRoutes,
  showRouteCount = false,
}: RouteMapDisplayProps) {
  const { projectSlug } = useNotificationProject();
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const { data: datesInfo } = useGetDatesQuery(projectSlug);
  const [layer, setLayer] = useState<number | null>(null);

  useEffect(() => {
    if (datesInfo && datesInfo.length > 0) {
      const latestLayer = datesInfo[datesInfo.length - 1]?.layer;
      if (latestLayer && latestLayer !== layer) {
        setLayer(latestLayer);
      }
    }
  }, [datesInfo, layer]);

  const layerContextValue = useMemo(() => ({ layer, setLayer }), [layer]);

  // when navigating directly to ex. /assign-routes page, userProject might not be loaded yet
  // so we need to wait before rendering the Map
  if (!userProject || !layer) {
    return null;
  }

  return (
    <LayerContext.Provider value={layerContextValue}>
      <MapContextProvider>
        <RouteMapDisplayInner
          selectedRoutes={selectedRoutes}
          availableRoutes={availableRoutes}
          hoveredRoutes={hoveredRoutes}
          showRouteCount={showRouteCount}
        />
      </MapContextProvider>
    </LayerContext.Provider>
  );
}
